/**
 * #5515 AMENDMENT v2 — THE SHARED TYPED DRIFT CONTRACT.
 *
 * One declaration of what a plan-versus-actual measure IS, and one set of
 * deterministic equations over it, so every module that monitors drift emits
 * the same shapes and the same verdict from the same inputs.
 *
 * The reason this is shared rather than per-module: a second module writing its
 * own variance arithmetic is a second definition of "behind", and two surfaces
 * would disagree about the same commitment while both looked governed. The
 * equations below are pure — no clock, no tenant, no wording, no I/O — so a
 * verdict is reproducible from the declaration and the observation alone.
 *
 * What this file does NOT do: it decides no narration, publishes no binding,
 * reads no evidence, and stamps no badge. A module supplies its declared
 * measure and its observed periods; it gets back typed values and a closed
 * status, which it then publishes through its own governed output adapter.
 */

/**
 * Which direction is good. A cost-reduction commitment is `lower_is_better`:
 * actual ABOVE plan is adverse. A revenue or realization commitment is
 * `higher_is_better`. Polarity is declared once, on the measure, because
 * inferring it from a metric name is exactly the string branch this codebase
 * forbids.
 */
export type GovernedDriftPolarity = 'lower_is_better' | 'higher_is_better';

/** The closed status vocabulary. Narration reads this; it never re-derives it. */
export type GovernedDriftStatus = 'on_track' | 'watch' | 'off_track';

export const GOVERNED_DRIFT_STATUSES: readonly GovernedDriftStatus[] = Object.freeze([
  'on_track',
  'watch',
  'off_track',
]);

/**
 * Why a period produced no status. A period that cannot be judged says so with
 * a typed reason — it never falls back to `on_track`, and it never reports a
 * zero variance it did not measure.
 */
export type GovernedDriftUnjudgedReason =
  | 'plan_value_absent'
  | 'actual_value_absent'
  | 'plan_value_zero'
  | 'period_unmatched';

/** Where one observed number came from, at the revision it was read. */
export type GovernedDriftSourceRevision = Readonly<{
  sourceId: string;
  sourceClass: string;
  schemaVersion: string;
  asOf: string;
}>;

/**
 * The measure's identity and its judging rule, declared together so a threshold
 * can never be read against a different unit than the one it was set in.
 */
export type GovernedDriftMeasureContract = Readonly<{
  measureId: string;
  label: string;
  unit: string;
  polarity: GovernedDriftPolarity;
  /** Scope the measure is stated over, e.g. a commitment or a cost pool. */
  scopeLabel: string;
  /**
   * Adverse variance, as a share of plan, at which the status leaves each band.
   * Both are exclusive lower bounds: exactly `watchAdverseRatio` is still
   * `on_track`, which keeps a threshold set at "1%" from firing at 1%.
   */
  thresholds: Readonly<{
    watchAdverseRatio: number;
    offTrackAdverseRatio: number;
  }>;
}>;

/** One period's plan and actual, with the rows they were read from. */
export type GovernedDriftPeriodObservation = Readonly<{
  periodKey: string;
  asOf: string;
  planValue: number | null;
  actualValue: number | null;
  sourceRevisions: readonly GovernedDriftSourceRevision[];
}>;

/**
 * The deterministic result for ONE period. `signedVariance` keeps its sign in
 * the measure's own direction (actual minus plan); `adverseVariance` restates
 * it so that positive always means "worse", whatever the polarity. Both are
 * published because a reader needs the direction and a threshold needs the
 * severity, and deriving one from the other downstream is where sign errors
 * live.
 */
export type GovernedDriftPeriodEvaluation = Readonly<{
  periodKey: string;
  asOf: string;
  planValue: number | null;
  actualValue: number | null;
  signedVariance: number | null;
  absoluteVariance: number | null;
  adverseVariance: number | null;
  adverseVariancePct: number | null;
  status: GovernedDriftStatus | null;
  unjudgedReason: GovernedDriftUnjudgedReason | null;
  sourceRevisions: readonly GovernedDriftSourceRevision[];
}>;

/** The rounding the equations publish at: cents for values, 2dp for percent. */
function roundValue(value: number): number {
  return Number(value.toFixed(6));
}

function roundPercent(value: number): number {
  return Math.round(value * 100) / 100;
}

function unjudged(
  observation: GovernedDriftPeriodObservation,
  reason: GovernedDriftUnjudgedReason,
  partial?: Partial<GovernedDriftPeriodEvaluation>,
): GovernedDriftPeriodEvaluation {
  return {
    periodKey: observation.periodKey,
    asOf: observation.asOf,
    planValue: observation.planValue,
    actualValue: observation.actualValue,
    signedVariance: null,
    absoluteVariance: null,
    adverseVariance: null,
    adverseVariancePct: null,
    status: null,
    unjudgedReason: reason,
    sourceRevisions: observation.sourceRevisions,
    ...partial,
  };
}

/**
 * Judge ONE period against the declared measure.
 *
 * Zero-plan behaviour is the case worth naming: a plan of zero has no share to
 * take a percentage of, so the period is returned UNJUDGED with
 * `plan_value_zero` and its signed and absolute variances still stated. It does
 * not become `off_track` by division, and it does not become `on_track` by
 * omission — the reader is told the threshold could not be applied.
 */
export function evaluateGovernedDriftPeriod(
  contract: GovernedDriftMeasureContract,
  observation: GovernedDriftPeriodObservation,
): GovernedDriftPeriodEvaluation {
  const { planValue, actualValue } = observation;
  if (planValue == null || !Number.isFinite(planValue)) {
    return unjudged(observation, 'plan_value_absent');
  }
  if (actualValue == null || !Number.isFinite(actualValue)) {
    return unjudged(observation, 'actual_value_absent');
  }

  const signedVariance = roundValue(actualValue - planValue);
  const absoluteVariance = roundValue(Math.abs(signedVariance));
  const adverseVariance = roundValue(
    contract.polarity === 'lower_is_better' ? signedVariance : -signedVariance,
  );

  if (planValue === 0) {
    return unjudged(observation, 'plan_value_zero', {
      signedVariance,
      absoluteVariance,
      adverseVariance,
    });
  }

  const adverseRatio = adverseVariance / Math.abs(planValue);
  return {
    periodKey: observation.periodKey,
    asOf: observation.asOf,
    planValue,
    actualValue,
    signedVariance,
    absoluteVariance,
    adverseVariance,
    adverseVariancePct: roundPercent(adverseRatio * 100),
    status: governedDriftStatusForRatio(contract, adverseRatio),
    unjudgedReason: null,
    sourceRevisions: observation.sourceRevisions,
  };
}

/**
 * The band boundaries, stated once. Both thresholds are EXCLUSIVE lower bounds,
 * so a measure sitting exactly on its watch threshold is still on track.
 */
export function governedDriftStatusForRatio(
  contract: GovernedDriftMeasureContract,
  adverseRatio: number,
): GovernedDriftStatus {
  if (adverseRatio > contract.thresholds.offTrackAdverseRatio) return 'off_track';
  if (adverseRatio > contract.thresholds.watchAdverseRatio) return 'watch';
  return 'on_track';
}

/**
 * The series verdict: the LATEST judged period's status, never a best or worst
 * across the window. A commitment's state is what it is now; a module that
 * wants the trend has every period's own status beside it.
 */
export function latestJudgedGovernedDriftPeriod(
  periods: readonly GovernedDriftPeriodEvaluation[],
): GovernedDriftPeriodEvaluation | null {
  for (let index = periods.length - 1; index >= 0; index -= 1) {
    if (periods[index].status !== null) return periods[index];
  }
  return null;
}
